/**
 * InfinitSales - Notification Panel
 * Flyout do sino: lista o feed, permite marcar lida / marcar todas / excluir.
 * Posicionado pelo proprio componente (ancora a' direita da sidebar, perto do
 * sino no rodape). Consome o NotificationsContext.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCheck, X, BellOff, Loader2, BellRing } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotifications } from '../../contexts/NotificationsContext';
import {
  shouldOfferNativeNotifications,
  requestNativeNotifications,
  dismissNativeNotificationsOffer,
} from '../../utils/nativeNotifications';
import { routeOf } from '../../utils/channels';

// Rótulos PT das categorias (chips). Novas categorias sem rótulo caem no id.
const CATEGORY_LABELS = {
  contact_transfer: 'Transferências',
  handoff: 'Atendimento',
  campaign: 'Campanhas',
  team: 'Equipe',
  user_note: 'Minhas notas',
  agent_training: 'Treinar agente',
  create_company: 'Criar empresa',
  product_update: 'Novidades',
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (isNaN(then)) return '';
  const diff = Math.max(0, Date.now() - then);
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `${min}min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(then).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

const NotificationPanel = ({ onClose, panelStyle }) => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const {
    notifications, unreadCount, markRead, markAllRead, remove, pushToast,
    activeCategory, availableCategories, setCategory, hasMore, loadingMore, loadMore,
  } = useNotifications();

  // Convite p/ notificações do dispositivo (avaliado 1x por abertura do painel).
  const [nativeOffer, setNativeOffer] = useState(() => shouldOfferNativeNotifications());

  // Clicar numa notificacao: marca como lida e roteia pelo CTA.
  //  - open_chat: navega pro inbox do canal com o chat alvo (o shell seleciona;
  //    se o contato ja saiu do workspace, o proprio inbox mostra o toast).
  //  - open_url: abre a rota interna / link externo.
  //  - sem destino (ex.: transferencia "voce perdeu o contato"): toast no canto.
  const handleOpen = (n) => {
    if (!n.read) markRead(n.id);
    const cta = n.cta;
    if (cta?.action === 'open_chat' && cta.payload) {
      const { channel, chat_id, contact_phone, contact_igsid, contact_psid } = cta.payload;
      onClose?.();
      // O ELSE mandava tudo que nao fosse wa_business para /inbox (WhatsApp
      // Pessoal) — uma transferencia de contato do Instagram/Messenger levava
      // o operador ao inbox errado, procurando um chat_id que nao existe la.
      // A rota vem do mapa de canais (desconhecido = /inbox, como antes).
      const rota = routeOf(channel);
      navigate(rota, {
        state: { openChat: { chat_id, contact_phone, contact_igsid, contact_psid, channel } },
      });
      return;
    }
    if (cta?.action === 'open_url' && cta.payload?.url) {
      const url = cta.payload.url;
      onClose?.();
      if (/^https?:\/\//i.test(url)) window.open(url, '_blank', 'noopener,noreferrer');
      else navigate(url);
      return;
    }
    // Sem destino navegável: contato transferido que saiu do meu workspace.
    if (n.category === 'contact_transfer') {
      onClose?.();
      pushToast?.({
        title: 'Contato transferido',
        body: 'Este contato saiu do seu workspace.',
        accentColor: '#2563EB',
      });
    }
  };

  const styles = {
    panel: {
      position: 'absolute',
      left: '56px',
      bottom: '0',
      width: '360px',
      maxWidth: 'calc(100vw - 80px)',
      maxHeight: '70vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: colors.bgPrimary,
      border: `1px solid ${colors.borderColor}`,
      borderRadius: '12px',
      boxShadow: '0 12px 32px rgba(0,0,0,0.28)',
      zIndex: 1000,
      overflow: 'hidden',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '12px 14px',
      borderBottom: `1px solid ${colors.borderColor}`,
    },
    title: { fontSize: '14px', fontWeight: 600, color: colors.textPrimary },
    markAll: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      border: 'none',
      background: 'transparent',
      color: colors.accentPrimary,
      fontSize: '12px',
      fontWeight: 600,
      cursor: 'pointer',
      padding: '4px 6px',
      borderRadius: '6px',
    },
    list: { overflowY: 'auto', flex: 1 },
    empty: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '32px 16px',
      color: colors.textTertiary,
      fontSize: '13px',
    },
    item: (unread) => ({
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      padding: '12px 14px',
      borderBottom: `1px solid ${colors.borderColor}`,
      cursor: 'pointer',
      backgroundColor: unread ? `${colors.accentPrimary}0d` : 'transparent',
      transition: 'background-color 0.15s',
    }),
    dot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      backgroundColor: colors.accentPrimary,
      marginTop: '6px',
      flexShrink: 0,
    },
    dotPlaceholder: { width: '8px', flexShrink: 0 },
    body: { flex: 1, minWidth: 0 },
    itemTitle: {
      fontSize: '13px',
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '2px',
    },
    itemText: {
      fontSize: '12px',
      color: colors.textSecondary,
      lineHeight: 1.4,
      overflowWrap: 'anywhere',
    },
    itemMeta: { fontSize: '11px', color: colors.textTertiary, marginTop: '4px' },
    removeBtn: {
      border: 'none',
      background: 'transparent',
      color: colors.textTertiary,
      cursor: 'pointer',
      padding: '2px',
      borderRadius: '4px',
      flexShrink: 0,
    },
  };

  return (
    <div style={{ ...styles.panel, ...(panelStyle || {}) }} onClick={(e) => e.stopPropagation()}>
      <div style={styles.header}>
        <span style={styles.title}>
          Notificações{unreadCount > 0 ? ` (${unreadCount})` : ''}
        </span>
        {unreadCount > 0 && (
          <button style={styles.markAll} onClick={markAllRead}>
            <CheckCheck size={14} />
            Marcar todas
          </button>
        )}
      </div>

      {/* Convite discreto: notificações do DISPOSITIVO (Android/desktop com o
          app em segundo plano). Pedido de permissão SÓ por gesto do usuário;
          some após decidir ou dispensar. */}
      {nativeOffer && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '8px 12px', borderBottom: `1px solid ${colors.borderColor}`,
        }}>
          <BellRing size={14} color={colors.accentPrimary} style={{ flexShrink: 0 }} />
          <span style={{ flex: 1, fontSize: 11, color: colors.textSecondary }}>
            Receber notificações no dispositivo quando o app estiver em segundo plano?
          </span>
          <button
            onClick={async () => { await requestNativeNotifications(); setNativeOffer(false); }}
            style={{
              padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600,
              cursor: 'pointer', border: 'none',
              backgroundColor: colors.accentPrimary, color: '#fff', whiteSpace: 'nowrap',
            }}
          >
            Ativar
          </button>
          <button
            onClick={() => { dismissNativeNotificationsOffer(); setNativeOffer(false); }}
            title="Agora não"
            style={{
              padding: 4, borderRadius: 6, cursor: 'pointer',
              border: 'none', background: 'transparent', color: colors.textTertiary,
              display: 'flex',
            }}
          >
            <X size={13} />
          </button>
        </div>
      )}

      {/* Chips de categoria — filtram o feed no servidor. Só aparecem quando há
          mais de uma categoria no feed (evita chip único inútil). */}
      {availableCategories.length > 1 && (
        <div style={{
          display: 'flex', gap: 6, flexWrap: 'wrap',
          padding: '8px 12px', borderBottom: `1px solid ${colors.borderColor}`,
        }}>
          {[{ id: null, label: 'Todas' },
            ...availableCategories.map((c) => ({ id: c, label: CATEGORY_LABELS[c] || c }))
          ].map((chip) => {
            const active = (activeCategory || null) === chip.id;
            return (
              <button
                key={chip.id || '__all'}
                onClick={() => setCategory(chip.id)}
                style={{
                  padding: '4px 10px', borderRadius: 14, fontSize: 11, fontWeight: 600,
                  cursor: 'pointer', whiteSpace: 'nowrap',
                  border: `1px solid ${active ? colors.accentPrimary : colors.borderColor}`,
                  backgroundColor: active ? colors.accentPrimary : 'transparent',
                  color: active ? '#fff' : colors.textSecondary,
                }}
              >
                {chip.label}
              </button>
            );
          })}
        </div>
      )}

      <div style={styles.list}>
        {notifications.length === 0 ? (
          <div style={styles.empty}>
            <BellOff size={28} />
            <span>Sem notificações por aqui</span>
          </div>
        ) : (
          notifications.map((n) => (
            <div
              key={n.id}
              style={styles.item(!n.read)}
              onClick={() => handleOpen(n)}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = colors.bgSecondary)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = !n.read
                  ? `${colors.accentPrimary}0d`
                  : 'transparent')
              }
            >
              {!n.read ? <span style={styles.dot} /> : <span style={styles.dotPlaceholder} />}
              <div style={styles.body}>
                <div style={styles.itemTitle}>{n.title}</div>
                {n.body && <div style={styles.itemText}>{n.body}</div>}
                <div style={styles.itemMeta}>
                  {n.contact_phone ? `+${n.contact_phone} · ` : ''}
                  {timeAgo(n.created_at)}
                </div>
              </div>
              <button
                style={styles.removeBtn}
                title="Remover"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(n.id);
                }}
              >
                <X size={14} />
              </button>
            </div>
          ))
        )}
        {hasMore && (
          <button
            onClick={loadMore}
            disabled={loadingMore}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              width: '100%', padding: '10px', border: 'none',
              borderTop: `1px solid ${colors.borderColor}`,
              background: 'transparent', color: colors.accentPrimary,
              fontSize: 12, fontWeight: 600, cursor: loadingMore ? 'wait' : 'pointer',
            }}
          >
            {loadingMore && <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />}
            {loadingMore ? 'Carregando…' : 'Carregar mais'}
          </button>
        )}
      </div>
    </div>
  );
};

export default NotificationPanel;
